import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchWithAuth } from '../services/AuthService';
import { Spinner, Table, Form, Button, ButtonGroup } from 'react-bootstrap';

const ROLES = ['user', 'admin', 'tester'];

// Formate une date ISO -> valeur pour <input type="date">  (YYYY-MM-DD)
const toDateInputValue = (isoDate) => {
  if (!isoDate) return '';
  const d = new Date(isoDate);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};


// Recalcul client-side du statut (même règle que côté backend), utilisé pour
// mettre à jour l'affichage immédiatement après une action, sans attendre un refetch.
const computeIsActive = (role, expireDate) => {
  if (role === 'admin') return true;
  if (!expireDate) return false;
  return new Date(expireDate).getTime() > Date.now();
};

// Filtre de statut affiché à l'écran -> valeur envoyée au backend (status query param)
const STATUS_FILTERS = [
  { value: 'all', label: 'Tous' },
  { value: 'active', label: 'Actifs' },
  { value: 'inactive', label: 'Inactifs' },
];

const AdminUsers = () => {
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('email'); // 'email' | 'createdAt' | 'expireDate'
  const [order, setOrder] = useState('asc');     // 'asc' | 'desc'
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'active' | 'inactive'
  const [savingId, setSavingId] = useState(null);
  const [message, setMessage] = useState('');
  // Valeur en cours d'édition dans le champ date, par utilisateur
  const [expireDateDrafts, setExpireDateDrafts] = useState({});

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ sortBy, order, status: statusFilter });
      const response = await fetchWithAuth(`/api/admin/users?${params.toString()}`);
      if (!response.ok) throw new Error('Erreur lors du chargement des utilisateurs');
      const data = await response.json();
      setUsers(data.users || []);
      setExpireDateDrafts(
        Object.fromEntries((data.users || []).map((u) => [u._id, toDateInputValue(u.expireDate)]))
      );
      setError(null);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Erreur de chargement');
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [sortBy, order, statusFilter, navigate]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setOrder('asc');
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    setSavingId(userId);
    setMessage('');
    try {
      const response = await fetchWithAuth(`/api/admin/users/${userId}/role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: newRole }),
      });
      const data = await response.json();

      if (!response.ok) {
        setMessage(`❌ ${data.message || "Erreur lors de la mise à jour."}`);
        return;
      }

      setUsers((prev) =>
        prev.map((u) =>
          u._id === userId
            ? { ...u, role: data.user.role, expireDate: data.user.expireDate, isActive: computeIsActive(data.user.role, data.user.expireDate) }
            : u
        )
      );
      setExpireDateDrafts((prev) => ({ ...prev, [userId]: toDateInputValue(data.user.expireDate) }));

      if (newRole === 'tester') {
        setMessage('✅ Rôle mis à jour. Accès aux fonctionnalités payantes accordé pour 90 jours.');
      } else {
        setMessage('✅ Rôle mis à jour.');
      }
    } catch (err) {
      console.error(err);
      setMessage('❌ Erreur réseau ou serveur.');
    } finally {
      setSavingId(null);
    }
  };

  const handleExpireDateSave = async (userId) => {
    setSavingId(userId);
    setMessage('');
    try {
      const draft = expireDateDrafts[userId];
      // draft vide -> on retire l'abonnement (expireDate = null)
      const expireDate = draft ? new Date(draft).toISOString() : null;

      const response = await fetchWithAuth(`/api/admin/users/${userId}/subscription`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ expireDate }),
      });
      const data = await response.json();

      if (!response.ok) {
        setMessage(`❌ ${data.message || "Erreur lors de la mise à jour de l'abonnement."}`);
        return;
      }

      setUsers((prev) =>
        prev.map((u) =>
          u._id === userId
            ? { ...u, expireDate: data.user.expireDate, isActive: computeIsActive(u.role, data.user.expireDate) }
            : u
        )
      );
      setMessage('✅ Date de fin d\'abonnement mise à jour.');
    } catch (err) {
      console.error(err);
      setMessage('❌ Erreur réseau ou serveur.');
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner animation="border" variant="primary" />
        <span className="ml-3">Chargement des utilisateurs...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-600 mt-10">
        <p>❌ {error}</p>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-6">
      <div className="flex justify-between items-center mb-4" style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center', justifyContent: 'space-between' }}>
        <ButtonGroup>
          <Button
            variant={sortBy === 'email' ? 'primary' : 'outline-primary'}
            onClick={() => handleSort('email')}
          >
            Trier par email {sortBy === 'email' ? (order === 'asc' ? '↑' : '↓') : ''}
          </Button>
          <Button
            variant={sortBy === 'createdAt' ? 'primary' : 'outline-primary'}
            onClick={() => handleSort('createdAt')}
          >
            Trier par date de création {sortBy === 'createdAt' ? (order === 'asc' ? '↑' : '↓') : ''}
          </Button>
          <Button
            variant={sortBy === 'expireDate' ? 'primary' : 'outline-primary'}
            onClick={() => handleSort('expireDate')}
          >
            Trier par fin d'abonnement {sortBy === 'expireDate' ? (order === 'asc' ? '↑' : '↓') : ''}
          </Button>
        </ButtonGroup>

        <Form.Group style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: 0 }}>
          <Form.Label style={{ marginBottom: 0 }}></Form.Label>
          <Form.Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ maxWidth: '160px' }}
          >
            {STATUS_FILTERS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </div>

      {message && (
        <p className={`text-center text-sm ${message.includes('✅') ? 'text-green-600' : 'text-red-600'}`}>
          {message}
        </p>
      )}

      {users.length === 0 ? (
        <p className="text-center text-muted">Aucun utilisateur ne correspond à ce filtre.</p>
      ) : (
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Email</th>
            <th>Créé le</th>
            <th>Rôle</th>
            <th>Fin d'abonnement</th>
            <th>Abonnement</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => {
            const isActive = user.isActive !== undefined ? user.isActive : computeIsActive(user.role, user.expireDate);
            return (
            <tr key={user._id}>
              <td>{user.email}</td>
              <td>{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'}</td>
              <td>
                <Form.Select
                  value={user.role}
                  disabled={savingId === user._id}
                  onChange={(e) => handleRoleChange(user._id, e.target.value)}
                  style={{ maxWidth: '160px' }}
                >
                  {ROLES.map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </Form.Select>
                {user.role === 'tester' && (
                  <Form.Text className="text-muted">Accès payant auto +90j</Form.Text>
                )}
              </td>
              <td>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <Form.Control
                    type="date"
                    size="sm"
                    style={{ maxWidth: '160px' }}
                    disabled={savingId === user._id}
                    value={expireDateDrafts[user._id] || ''}
                    onChange={(e) =>
                      setExpireDateDrafts((prev) => ({ ...prev, [user._id]: e.target.value }))
                    }
                  />
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    disabled={savingId === user._id}
                    onClick={() => handleExpireDateSave(user._id)}
                  >
                    Enregistrer
                  </Button>
                </div>
              </td>
              <td>
                <span className={isActive ? 'text-green-600' : 'text-red-600'}>
                  {isActive ? '✅ Actif' : '⛔ Inactif'}
                </span>
              </td>
            </tr>
            );
          })}
        </tbody>
      </Table>
      )}
    </div>
  );
};

export default AdminUsers;